import React from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Card, Typography, Button, Descriptions, Space, Tag, Avatar, Badge } from 'antd';
import * as Icon from 'react-feather';
import valuers, { valuationConfigs as sampleConfigs } from './valuersData';

const { Title, Text } = Typography;

const templateMap = {
  'T-RES': 'Residential Template',
  'T-VEH': 'Vehicle Template',
  'T-COM': 'Commercial Template',
};

const maxPeriodMap = {
  '7': '7 days',
  '14': '14 days',
  '30': '30 days',
  '90': '90 days',
  '365': '1 year',
};

const riskActionMap = {
  auto: 'Auto raise',
  note: 'Note',
  ids: 'IDs',
};

export default function ViewValuationConfig() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const navConfig = location?.state?.config;
  const config = navConfig || sampleConfigs.find((c) => c.id === id);

  const getStatusColor = (status) => {
    const map = {
      active: '#52c41a',
      pending: '#faad14',
      inactive: '#ff4d4f',
    };
    return map[status?.toLowerCase()] || '#1890ff';
  };

  const resolveValuer = (id) => {
    if (!id) return null;
    return valuers.find((v) => v.id === id) || null;
  };

  const formatInitiation = (arr) => {
    if (!arr || arr.length === 0) return 'None';
    const map = {
      new_policy: 'New Policy',
      renewals: 'Renewals',
    };
    return arr.map((a) => map[a] || a).join(', ');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: 24 }}>
      {!config ? (
        <Card style={{ borderRadius: 12 }}>
          <div style={{ padding: 24 }}>
            <Text type="secondary">No valuation configuration found for id:</Text>
            <Title level={4} style={{ marginTop: 8 }}>{id}</Title>
            <div style={{ marginTop: 12 }}>
              <Button onClick={() => history.goBack()}>Back</Button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card
            style={{ marginBottom: 24, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: 'none' }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 14, display: 'block' }}>Valuation Configuration</Text>
                  <Title level={3} style={{ margin: 0, color: '#1890ff', fontWeight: 600 }}>{config.name}</Title>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Badge color={getStatusColor(config.status)} text={<Text strong style={{ textTransform: 'uppercase' }}>{(config.status || 'N/A')}</Text>} />
                  <Space>
                    <Button type="primary" icon={<Icon.Edit size={14} />} onClick={() => alert('Edit not implemented')}>Edit</Button>
                    <Button onClick={() => history.goBack()} icon={<Icon.ArrowLeft size={14} />}>Back</Button>
                  </Space>
                </div>
              </div>
            }
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flex: 1 }}>
                <Avatar size={72} style={{ backgroundColor: '#e6f7ff', color: '#001529' }}>
                  <Icon.FileText size={28} />
                </Avatar>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{config.name}</div>
                  <div style={{ color: '#6b7280', marginTop: 6 }}>{templateMap[config.template] || 'Custom Template'}</div>
                </div>
              </div>

              <div style={{ textAlign: 'right', minWidth: 180 }}>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Created</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>{config.createdAt ? new Date(config.createdAt).toLocaleDateString() : 'N/A'}</div>
              </div>
            </div>
          </Card>

          <Card style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Descriptions
              title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Icon.FileText size={18} style={{ color: '#1890ff' }} /><span style={{ fontWeight: 600 }}>Configuration Details</span></div>}
              bordered
              column={2}
              size="small"
              style={{ background: '#fff', borderRadius: 8 }}
            >
              <Descriptions.Item label="ID">{config.id}</Descriptions.Item>
              <Descriptions.Item label="Created By">{config.createdBy || 'N/A'}</Descriptions.Item>

              <Descriptions.Item label="Name">{config.name}</Descriptions.Item>
              <Descriptions.Item label="Status">{config.status || 'N/A'}</Descriptions.Item>

              <Descriptions.Item label="Template">{templateMap[config.template] || config.template || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Maximum Period">{maxPeriodMap[config.maxPeriod] || config.maxPeriod || 'N/A'}</Descriptions.Item>

              <Descriptions.Item label="Client Can Appoint">{
                typeof config.clientCanAppoint === 'boolean'
                  ? (config.clientCanAppoint ? 'Yes' : 'No')
                  : (config.clientCanAppoint || 'N/A')
              }</Descriptions.Item>

              <Descriptions.Item label="Request Initiation" span={2}>{formatInitiation(config.requestInitiation)}</Descriptions.Item>

              <Descriptions.Item label="Default Valuer">{
                (() => {
                  const v = resolveValuer(config.defaultValuer);
                  return v ? `${v.name} (${v.contacts})` : (config.defaultValuer ? config.defaultValuer : 'None');
                })()
              }</Descriptions.Item>

              <Descriptions.Item label="Risk Value Action">{riskActionMap[config.riskValueAction] || config.riskValueAction || 'N/A'}</Descriptions.Item>

              <Descriptions.Item label="Panel Members" span={2}>
                {Array.isArray(config.panelMembers) && config.panelMembers.length > 0 ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {config.panelMembers.map((m) => (
                      <Tag key={m} color="blue">{m}</Tag>
                    ))}
                  </div>
                ) : 'None'}
              </Descriptions.Item>

              <Descriptions.Item label="Description" span={2}>{config.description || 'No description'}</Descriptions.Item>

              <Descriptions.Item label="Created At" span={2}>{config.createdAt ? new Date(config.createdAt).toLocaleString() : 'N/A'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )}
    </div>
  );
}
